/**
 * The Webhooks tab's view of the backend — `/v1/communities/{id}/webhooks`.
 *
 * ONE WEBHOOK IS TWO THINGS ON THE SERVER, and this module keeps that seam where it
 * belongs: an ENDPOINT is the address and its signing secret, a TRIGGER is one event
 * pointed at it. An owner thinks "this webhook fires when X and when Y", so a `Webhook`
 * carries its own `triggers` and every call here is addressed to the endpoint.
 *
 * THE SECRET ARRIVES EXACTLY ONCE, on `createWebhook`. No other call returns it and no
 * route exists to read it back, so whatever renders it is the last chance anyone has to
 * copy it — say so in the UI rather than offering a reveal that cannot work.
 */
import { apiGet, apiSend } from './transport';

/** Something a webhook can fire on, as the picker renders it. */
export interface EventOption {
  name: string;
  label: string;
  describe: string;
  /** Whether one member can cross it repeatedly — "every time" vs "once, when they join". */
  recurring: boolean;
}

export interface WebhookTrigger {
  key: string;
  name: string;
  event: string;
  event_label: string;
  enabled: boolean;
  /** The no-backfill floor: nothing before this is ever delivered. Re-stamped on enable. */
  enabled_at: string | null;
  condition: Record<string, unknown> | null;
}

/** Whether an endpoint is actually receiving. `last_status` is one integer with no clock
 *  beside it, so an endpoint that answered a minute ago and one that has refused
 *  everything since Tuesday read the same; this carries the age of the last delivery the
 *  receiver ACCEPTED. Optional — additive on the server. */
export interface WebhookHealth {
  status: 'ok' | 'failing' | 'parked';
  last_ok_at?: string | null;
  last_ok_age_secs?: number | null;
  failing_for_secs?: number | null;
  parked_reason?: string | null;
}

export interface Webhook {
  key: string;
  name: string;
  url: string;
  enabled: boolean;
  last_status: number | null;
  last_delivery_at: string | null;
  health?: WebhookHealth | null;
  /** Set when WE turned this endpoint off, and why — `enabled: false` alone cannot tell
   *  an owner whether they switched it off last month or we did this morning. */
  parked_at?: string | null;
  parked_reason?: string | null;
  /** Why the last attempt failed, as one of the server's stable reasons — the one thing
   *  `last_status` cannot say, because `0` covers every refusal made before the request
   *  left. `refused_no_secret` is the blocking one: a signing secret we hold and can no
   *  longer decrypt, which is ours and not the receiver's. Null once a delivery
   *  succeeds. */
  last_error?: string | null;
  triggers: WebhookTrigger[];
}

/** The create response — the only shape that ever carries the signing secret. */
export interface CreatedWebhook extends Webhook {
  secret: string;
}

/** How a receiver proves a delivery came from us. Served, never hardcoded here. */
export interface SignatureInfo {
  header: string;
  format: string;
  signed_material: string;
  algorithm: string;
  /** The header that names one attempt at one delivery, so a receiver can dedupe. Not
   *  part of the signed material — the signature is what proves where a delivery came
   *  from; this only says "you may have seen this one". Optional because it arrived
   *  after the four fields above. */
  delivery_header?: string | null;
  delivery_format?: string | null;
  delivery_note?: string | null;
}

export interface WebhookList {
  items: Webhook[];
  total: number | null;
  events: EventOption[];
  /** One illustrative delivery, envelope included. Synthetic values — it gets pasted
   *  into third-party chat windows. */
  sample: unknown;
  signature: SignatureInfo | null;
}

export interface Delivery {
  id: number;
  event: string;
  /** `0` when the attempt never became a request at all; null when it left and nothing
   *  came back. The two are different things to do about it, so they read differently. */
  status_code: number | null;
  /** One of the server's stable reasons — feed it to {@link deliveryReason}, never
   *  render it raw. */
  error: string | null;
  duration_ms: number | null;
  created_at: string | null;
  /** Which try this was at the same delivery, counting from 1. Null for a manual test,
   *  which belongs to no run and is never retried. Optional — additive on the server. */
  attempt?: number | null;
  /** What `Sideguide-Delivery` carried on this attempt — the id the RECEIVER saw, and
   *  the only handle both ends share when somebody says "I never got it". */
  delivery_id?: string | null;
}

export interface DeliveryList {
  items: Delivery[];
  total: number | null;
}

export interface TestResult {
  ok: boolean;
  status_code: number | null;
  error: string | null;
  duration_ms: number | null;
  /** Which member we described, so an owner can check the body against a real person. */
  sample_member: string | null;
}

const base = (skoolId: number): string => `/v1/communities/${skoolId}/webhooks`;

export function fetchWebhooks(skoolId: number): Promise<WebhookList> {
  return apiGet<WebhookList>(base(skoolId));
}

export function createWebhook(
  skoolId: number,
  body: { name: string; url: string },
): Promise<CreatedWebhook> {
  return apiSend<CreatedWebhook>('POST', base(skoolId), body);
}

export function deleteWebhook(skoolId: number, key: string): Promise<WebhookList> {
  return apiSend<WebhookList>('DELETE', `${base(skoolId)}/${encodeURIComponent(key)}`);
}

export function setWebhookEnabled(
  skoolId: number,
  key: string,
  on: boolean,
): Promise<Webhook> {
  const verb = on ? 'enable' : 'disable';
  return apiSend<Webhook>('POST', `${base(skoolId)}/${encodeURIComponent(key)}/${verb}`);
}

/** Send one REAL delivery to the configured URL, signed, describing a real member. */
export function testWebhook(skoolId: number, key: string): Promise<TestResult> {
  return apiSend<TestResult>('POST', `${base(skoolId)}/${encodeURIComponent(key)}/test`);
}

export function fetchDeliveries(skoolId: number, key: string): Promise<DeliveryList> {
  return apiGet<DeliveryList>(`${base(skoolId)}/${encodeURIComponent(key)}/deliveries`);
}

export function addTrigger(
  skoolId: number,
  key: string,
  body: { event: string; name?: string; condition?: Record<string, unknown> | null },
): Promise<Webhook> {
  return apiSend<Webhook>('POST', `${base(skoolId)}/${encodeURIComponent(key)}/triggers`, body);
}

export function setTriggerEnabled(
  skoolId: number,
  triggerKey: string,
  on: boolean,
): Promise<WebhookTrigger> {
  const verb = on ? 'enable' : 'disable';
  return apiSend<WebhookTrigger>(
    'POST', `${base(skoolId)}/triggers/${encodeURIComponent(triggerKey)}/${verb}`);
}

export function deleteTrigger(skoolId: number, triggerKey: string): Promise<WebhookTrigger> {
  return apiSend<WebhookTrigger>(
    'DELETE', `${base(skoolId)}/triggers/${encodeURIComponent(triggerKey)}`);
}

/**
 * Narrow a trigger to specific destination roles, in the pipelines' own condition
 * language — the same evaluator the CRM boards use, so there is no second vocabulary.
 *
 * DESTINATION ONLY. The condition is evaluated against the member at the moment the
 * event fires, so it can say "…and they are now a Key voice" but never "…and they used
 * to be a Spectator". A receiver that needs the origin reads `contributor_role_previous`
 * off the payload, which is carried for exactly this reason.
 *
 * An empty selection is `null`: no condition at all, meaning every role change fires.
 */
export function roleCondition(roles: string[]): Record<string, unknown> | null {
  if (roles.length === 0) return null;
  return { op: 'in', signal: 'contributor.role', value: roles };
}

/** The roles a `member.role_changed` trigger may be narrowed to, in the canonical order. */
export const ROLE_CHANGED_EVENT = 'member.role_changed';

/** A delivery's health, as a one-word status for a badge. */
export function deliveryHealth(status: number | null): 'ok' | 'failed' | 'unknown' {
  if (status === null) return 'unknown';
  return status >= 200 && status < 300 ? 'ok' : 'failed';
}

/**
 * Why one attempt failed, in words.
 *
 * The server's vocabulary (`extractor/webhook_out.REASONS`) is a wire format — stable,
 * stored, and written for a machine. `refused_address` is exact and means nothing to the
 * person who typed the URL, so every reason has a sentence here, and it says which END
 * the problem is at: the ones beginning "we" are ours to fix, the rest are the
 * receiver's.
 *
 * An unknown reason falls back to its own name rather than to silence — a reason added
 * on the server later reads technically here instead of vanishing.
 */
const DELIVERY_REASONS: Record<string, string> = {
  // Ours: the address, or something about our own configuration.
  refused_scheme: 'We only deliver over https — this address is not.',
  refused_port: 'We only deliver to port 443.',
  refused_host: 'We could not look that hostname up.',
  refused_address:
    'That hostname points at a private or local address, which we will not call.',
  refused_body_too_large: 'The body was too large to send.',
  refused_no_secret:
    'We could not read this webhook’s signing secret, so nothing was signed or sent.',
  refused_endpoint_disabled: 'This webhook was switched off, so nothing was sent.',
  refused_no_endpoint: 'The trigger pointed at a webhook that no longer exists.',
  refused_rule_disabled: 'The trigger was switched off before this went out.',
  refused_subject_gone: 'The member had already been removed, so there was nothing to send.',
  // Theirs: the receiver, or the path to it.
  connect_failed: 'We could not reach your server.',
  tls_failed: 'Your server’s certificate could not be verified.',
  timed_out: 'Your server did not answer in time.',
  protocol_error: 'Your server’s answer was not valid HTTP.',
  http_error: 'Your server refused the delivery.',
  unknown_error: 'Something went wrong on the way — we could not say what.',
};

export function deliveryReason(reason: string | null | undefined): string {
  if (!reason) return '';
  return DELIVERY_REASONS[reason] ?? reason.replace(/_/g, ' ');
}

/** What an unhealthy endpoint says about itself — the state, what it means, and the one
 *  action that clears it. `ok` returns null: a healthy endpoint explains nothing. */
export interface EndpointCopy {
  label: string;
  what: string;
  fix: string;
}

export function endpointCopy(w: Webhook): EndpointCopy | null {
  // A SECRET WE CANNOT READ IS BLOCKING, and it outranks everything else: no delivery
  // can be signed, so none can be sent, and no amount of fixing the receiver helps. It
  // is also the one failure whose fix is destructive, so it is spelled out.
  if (w.last_error === 'refused_no_secret') {
    return {
      label: 'Cannot send',
      what: 'We can no longer read this webhook’s signing secret, so nothing can be '
        + 'signed and nothing is being delivered.',
      fix: 'Delete this webhook and add it again — you will get a new signing secret to '
        + 'put into your receiver.',
    };
  }
  if (w.health?.status === 'parked') {
    return {
      label: 'Paused by us',
      what: 'This receiver refused every delivery for a day, so we stopped calling it. '
        + 'Nothing is being sent.',
      fix: 'Fix the receiver, then re-enable this webhook.',
    };
  }
  if (w.health?.status === 'failing') {
    return {
      label: 'Failing',
      what: 'Your receiver is refusing our deliveries. We keep trying, and we stop after '
        + 'a day of it.',
      fix: 'Check the log below — it says what came back each time.',
    };
  }
  return null;
}

/** A duration in seconds as a short age — "3 days", "4 hours". The server sends ages in
 *  seconds beside the instants they were measured from, and an age is what a reader of
 *  "failing for…" actually wants. */
export function ageWords(secs: number | null | undefined): string {
  if (secs === null || secs === undefined) return '';
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;
  if (secs < 90) return 'under a minute';
  const m = Math.floor(secs / 60);
  if (m < 60) return plural(m, 'minute');
  const h = Math.floor(m / 60);
  if (h < 48) return plural(h, 'hour');
  return plural(Math.floor(h / 24), 'day');
}


/**
 * The prompt an owner pastes into an AI to have their receiver written.
 *
 * It carries the four things somebody writing a handler gets wrong without being told:
 * the ENVELOPE (`{event, data}`, not the member on its own), that every key is present
 * and may be null, how the signature is actually computed, and that we retry — so the
 * handler has to be idempotent. Everything here is a fact about our implementation, not
 * advice: `MAX_ATTEMPTS` really is 3, and the same delivery really can arrive twice.
 */
export function aiPrompt(sample: unknown, sig: SignatureInfo | null): string {
  const lines = [
    'I need to receive and verify a webhook. Write me an HTTP handler.',
    '',
    'The sender POSTs JSON with this exact body shape:',
    '',
    '```json',
    JSON.stringify(sample, null, 2),
    '```',
    '',
    'About the body:',
    '- `data` is FLAT. Every key is always present, and any of them may be null.',
    '- `intake_N_question` / `_type` / `_answer` are the answers this member gave to the',
    '  community\'s join questions; `intake_count` says how many there are.',
    '- Money is in minor units (e.g. plan_amount_cents 4900 = $49.00).',
    '- Timestamps are UTC ISO-8601 strings.',
    '- `event` is one of: member.joined, member.left, member.role_changed.',
    '- On member.role_changed, `contributor_role` is where they landed and',
    '  `contributor_role_previous` is where they came from.',
    '',
  ];
  if (sig) {
    lines.push(
      'Every request is signed. Verify it BEFORE parsing the body:',
      `- Header \`${sig.header}\` looks like \`${sig.format}\`.`,
      `- Compute ${sig.algorithm} over ${sig.signed_material}, keyed with my signing secret.`,
      '- Compare in constant time, and reject anything whose timestamp is over 5 minutes old.',
      '',
    );
  }
  if (sig?.delivery_header) {
    lines.push(
      'Every request also carries a delivery id, which is how you dedupe:',
      `- Header \`${sig.delivery_header}\` looks like \`${sig.delivery_format}\`.`,
      `- ${sig.delivery_note}`,
      '',
    );
  }
  lines.push(
    'Also please:',
    '- Return 2xx quickly. A non-2xx is retried up to 3 times, then given up on.',
    '- Make it idempotent — the same delivery can arrive more than once.',
    '',
    'What I want it to do with each event: <describe what you want here>',
  );
  return lines.join('\n');
}
